import {
  setupMultiApiClient,
  createTokenStorage,
  createHeaderRule,
  getEndpointsForEnv,
} from "@oneapp/data";
import { createDoubtsApiConfig } from "./buildEndpoints";

export interface AccountsData {
  access_token: string;
  refresh_token: string;
  is_user_existing: boolean;
  unified_accounts: {
    unified_account_id: string;
    first_name: string;
    last_name: string;
    grade?: number;
    user_account_photo?: string | null;
  }[];
}

export interface ProfileItem {
  uam_id?: number;
  admission_id?: string;
  crn_id?: string;
  branch_id?: string;
  tenant_id?: string;
  vertical_id?: number;
  is_default_profile?: boolean;
  is_temp_password?: boolean;
  tenant_code?: string;
  exams?: { examId: number; name: string }[];
  [key: string]: unknown;
}

export interface ProfilesData {
  access_token?: string;
  refresh_token?: string;
  response?: string;
  unified_account_details: {
    unified_account_id?: string;
    profiles?: ProfileItem[];
    user_account_details?: Record<string, unknown>;
    [key: string]: unknown;
  };
}

export interface EnrolledProgram {
  program_id?: string;
  program_name?: string;
  gradeId?: number;
  examId?: number;
  gradeName?: string;
  examName?: string;
  batch?: string | null;
  tenant_id?: string;
  subtenant_id?: number;
  vertical_id?: number;
  subjects?: unknown[];
  mdsExamName?: string;
  mdsExamId?: number;
  mdsGradeName?: string;
  mdsGradeId?: number;
  duration_in_weeks?: number;
  mdsOfferingName?: string;
  mdsOfferingId?: number;
  programOfferingIds?: number[];
  isDefault?: boolean;
  [key: string]: unknown;
}

export interface ModuleDataDto {
  accessToken: string | null;
  refreshToken: string | null;
  accountsData: AccountsData | null;
  profilesData: ProfilesData | null;
  enrolledPrograms: EnrolledProgram[] | null;
  /** Optional QB context for autosolve `qbId` when host embeds doubts from question bank. */
  qbId?: number | null;
}

export const defaultModuleDataDto: ModuleDataDto = {
  accessToken: null,
  refreshToken: null,
  accountsData: null,
  profilesData: null,
  enrolledPrograms: null,
};

/** Latest DTO for token + header rules (updated on each `initializeApi` call). */
let getModuleData: () => ModuleDataDto = () => defaultModuleDataDto;

function buildHeaderRules() {
  const defaultProfile = () => {
    const d = getModuleData();
    const profiles = d.profilesData?.unified_account_details?.profiles;
    return profiles?.find((p) => p.is_default_profile) ?? profiles?.[0];
  };

  const currentProgram = () => {
    const d = getModuleData();
    return (
      d.enrolledPrograms?.find((p) => p.isDefault) ?? d.enrolledPrograms?.[0]
    );
  };

  const getTenant = (): string =>
    (defaultProfile()?.tenant_code as string) ?? "infinitylearn";

  const getDeviceId = (): string =>
    (typeof localStorage !== "undefined" && localStorage.getItem("deviceIp")) ||
    "127.0.0.1";

  const getUserId = (): string | null =>
    defaultProfile()?.uam_id != null ? String(defaultProfile()!.uam_id) : null;

  const getUserDetails = (): {
    subTenant?: string | number;
    tenantId?: string | number;
  } | null => {
    const profile = defaultProfile();
    return profile
      ? {
          tenantId: profile.tenant_id,
          subTenant: currentProgram()?.subtenant_id,
        }
      : null;
  };

  const getProgramTenantForHeaders = (): string => {
    const prog = currentProgram();
    if (prog?.tenant_id != null && String(prog.tenant_id).trim() !== "") {
      return String(prog.tenant_id);
    }
    return defaultProfile()?.tenant_id?.toString() ?? "";
  };

  const getAccessTokenFromDto = (): string | null =>
    getModuleData().accessToken;

  const getBearerFromDto = (): string | null => {
    const t = getAccessTokenFromDto();
    return t ? `Bearer ${t}` : null;
  };

  return [
    createHeaderRule({
      pattern: /one_asm_api|one_asm_asset_api|proctoringUrl|geojs/,
      remove: [
        "X-Tenant",
        "ot-tenant",
        "X-Platform",
        "X-DeviceId",
        "Authorization",
      ],
      priority: 100,
    }),
    createHeaderRule({
      pattern: /api\.oneapp\.com/,
      add: {
        "X-Tenant": () => getTenant(),
        "ot-tenant": () => getTenant(),
        "X-Platform": "web",
        "X-DeviceId": () => getDeviceId(),
      },
      priority: 50,
    }),
    // Skip dcapi/doubtsai hosts — they use dedicated rules (production-aligned minimal headers).
    createHeaderRule({
      pattern:
        /https?:\/\/(?!dcapi(?:-staging)?\.)(?!doubtsai(?:-staging)?\.)([^/]+\.)*(infinitylearn\.com|devinfinitylearn\.in)/,
      add: {
        "X-Tenant": () => getTenant(),
        "ot-tenant": () => getTenant(),
        "X-Platform": "web",
        "X-DeviceId": () => getDeviceId(),
      },
      priority: 50,
    }),
    createHeaderRule({
      pattern: /doubtsai(-staging)?\.(devinfinitylearn\.in|infinitylearn\.com)/,
      add: {
        isMultiSessionRequired: "true",
        platform: "web",
        "x-platform": "web",
        "x-tenant": () => getTenant(),
        "ot-tenant": () => getTenant(),
        tenant: () => getTenant(),
        tenentId: () => getProgramTenantForHeaders(),
        accesstoken: () => getAccessTokenFromDto(),
        Authorization: () => getBearerFromDto() ?? "",
        "X-DeviceId": () => getDeviceId(),
      },
      priority: 55,
    }),

    createHeaderRule({
      pattern: /dcapi(-staging)?\.(devinfinitylearn\.in|infinitylearn\.com)/,
      add: {
        Accept: "application/json, text/plain, */*",
        Authorization: () => getBearerFromDto() ?? "",
        "ot-tenant": () => getTenant(),
        platform: "web",
        tenant: () => getTenant(),
        "x-platform": "web",
        "x-tenant": () => getTenant(),
        "x-deviceid": () => getDeviceId(),
      },
      priority: 55,
    }),
    createHeaderRule({
      pattern: /selflearnapi/,
      add: {
        subTenantId: () => getUserDetails()?.subTenant?.toString() ?? null,
        tenantId: () => getUserDetails()?.tenantId?.toString() ?? null,
      },
      priority: 50,
    }),
    createHeaderRule({
      pattern: /lcapi\.oneapp\.com/,
      add: {
        "X-Tenant": () => getTenant(),
        "ot-tenant": () => getTenant(),
        "X-Platform": "web",
        "X-DeviceId": () => getDeviceId(),
        userid: () => getUserId(),
      },
      priority: 50,
    }),
    createHeaderRule({
      pattern: /api\/selflearn\/getMapping/,
      remove: ["X-Tenant", "ot-tenant", "X-Platform", "X-DeviceId"],
      priority: 100,
    }),
    createHeaderRule({
      pattern: /myactivity\.devinfinitylearn\.in/,
      add: {
        "X-Tenant": "infinitylearn",
        "ot-tenant": "infinitylearn",
        "X-Platform": "web",
        "X-DeviceId": () => getDeviceId(),
        accesstoken: () => getModuleData().accessToken,
        refreshtoken: () => getModuleData().refreshToken,
      },
      priority: 50,
    }),
  ];
}

export function initializeApi(
  env: string = "production",
  dataDTO: ModuleDataDto = defaultModuleDataDto,
): void {
  getModuleData = () => dataDTO;
  const apiEndpoints = getEndpointsForEnv(env);

  setupMultiApiClient({
    apiConfig: createDoubtsApiConfig(apiEndpoints),
    tokenStorage: createTokenStorage({
      getAccessToken: () => getModuleData().accessToken,
      getRefreshToken: () => getModuleData().refreshToken,
      setTokens: () => {},
      clearTokens: () => {},
    }),
    refreshTokenFn: async () => {
      throw new Error("Doubts module: no refresh token configured");
    },
    onAuthFailure: () => {},
    onError: () => {},
    enableLogging: env === "development" || env === "staging",
    retry: {
      count: 2,
      delay: 1000,
      statusCodes: [408, 500, 502, 503, 504],
    },
    timeout: 30000,
    crossTab: { enabled: false },
    headerRules: buildHeaderRules(),
  });
}
